const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('authToken');
    // Cache for courses (5 minute TTL)
    this.coursesCache = {
      data: null,
      timestamp: null,
      ttl: 5 * 60 * 1000 // 5 minutes in milliseconds
    };
  }

  // Set auth token
  setAuthToken(token) {
    this.token = token;
    localStorage.setItem('authToken', token);
  }

  // Clear auth token
  clearAuthToken() {
    this.token = null;
    localStorage.removeItem('authToken');
  }

  // Get auth headers
  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (this.token) {
      headers['Authorization'] = `Token ${this.token}`;
    }
    
    return headers;
  }

  // Generic API request method
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      headers: this.getAuthHeaders(),
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      // Check if response has content to parse
      const contentType = response.headers.get('content-type');
      const contentLength = response.headers.get('content-length');
      
      // If no content or not JSON, return empty object
      if (contentLength === '0' || response.status === 204 || !contentType?.includes('application/json')) {
        return {};
      }

      // Try to parse JSON, return empty object if it fails
      try {
        return await response.json();
      } catch (jsonError) {
        console.warn('Failed to parse JSON response:', jsonError);
        return {};
      }
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Public API request method (no authentication required)
  async publicRequest(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      // Check if response has content to parse
      const contentType = response.headers.get('content-type');
      const contentLength = response.headers.get('content-length');
      
      // If no content or not JSON, return empty object
      if (contentLength === '0' || response.status === 204 || !contentType?.includes('application/json')) {
        return {};
      }

      // Try to parse JSON, return empty object if it fails
      try {
        return await response.json();
      } catch (jsonError) {
        console.warn('Failed to parse JSON response:', jsonError);
        return {};
      }
    } catch (error) {
      console.error('Public API request failed:', error);
      
      // For public endpoints, we should provide fallback data instead of throwing
      // This ensures the landing page still works even if backend is down
      if (endpoint.includes('/courses/')) {
        console.warn('Backend unavailable, using fallback course data');
        return this.getFallbackCourses();
      }
      
      throw error;
    }
  }

  // Fallback course data when backend is unavailable
  getFallbackCourses() {
    return [
      {
        id: 'fallback-1',
        name: 'Web Development Fundamentals',
        description: 'Learn the basics of web development with HTML, CSS, and JavaScript',
        duration_months: 3,
        cost_amount: 150000,
        cost_currency: 'NGN',
        total_students_enrolled: 0,
        instructor_name: 'Aitech Instructor'
      },
      {
        id: 'fallback-2',
        name: 'Python Programming',
        description: 'Master Python programming from beginner to advanced level',
        duration_months: 4,
        cost_amount: 200000,
        cost_currency: 'NGN',
        total_students_enrolled: 0,
        instructor_name: 'Aitech Instructor'
      },
      {
        id: 'fallback-3',
        name: 'Data Science & Analytics',
        description: 'Learn data science, machine learning, and analytics',
        duration_months: 6,
        cost_amount: 300000,
        cost_currency: 'NGN',
        total_students_enrolled: 0,
        instructor_name: 'Aitech Instructor'
      }
    ];
  }

  // AUTH ENDPOINTS
  async login(email, password) {
    // Ensure we don't send an old/invalid token in the header during login
    this.clearAuthToken();

    const response = await this.publicRequest('/auth/token/login/', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    if (response.auth_token) {
      this.setAuthToken(response.auth_token);
      // Store student ID for easy access
      const profile = await this.getUserProfile();
      if (profile.student_id) {
        localStorage.setItem('studentId', profile.student_id);
      }
    }
    
    return response;
  }

  // Student registration with email and password
  async registerStudent(studentData) {
    return this.publicRequest('/accounts/register/', {
      method: 'POST',
      body: JSON.stringify(studentData),
    });
  }

  // Password management
  async changePassword(oldPassword, newPassword) {
    return this.request('/accounts/password/change/', {
      method: 'POST',
      body: JSON.stringify({
        old_password: oldPassword,
        new_password: newPassword,
        re_new_password: newPassword
      }),
    });
  }

  async requestPasswordReset(email) {
    return this.publicRequest('/accounts/password/reset/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async confirmPasswordReset(token, newPassword) {
    return this.publicRequest('/accounts/password/reset/confirm/', {
      method: 'POST',
      body: JSON.stringify({
        token,
        new_password: newPassword,
        re_new_password: newPassword
      }),
    });
  }

  async logout() {
    try {
      await this.request('/auth/token/logout/', {
        method: 'POST',
      });
    } finally {
      this.clearAuthToken();
    }
  }

  async getUserProfile() {
    return this.request('/accounts/profile/');
  }

  async updateUserProfile(data) {
    return this.request('/accounts/profile/', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async changePassword(data) {
    return this.request('/accounts/password/change/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // PUBLIC STUDENT REGISTRATION
  async registerStudent(data) {
    return this.request('/accounts/register/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // DEV: Simulate payment confirmation via webhook
  async simulatePaymentWebhook(studentId, amount, txRef = null) {
    const payload = {
      tx_ref: txRef || `dev-${Date.now()}`,
      status: 'successful',
      amount,
      meta: { student_id: studentId },
    };
    return this.request('/accounts/payment-webhook/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // FLUTTERWAVE REAL PAYMENT
  async initFlutterwavePayment(studentId, amount, currency = 'NGN') {
    return this.request('/accounts/payments/flutterwave/init/', {
      method: 'POST',
      body: JSON.stringify({ student_id: studentId, amount, currency }),
    });
  }

  // STUDENT-SPECIFIC ENDPOINTS

  // Get student's enrolled courses with progress
  async getStudentCourses(studentId = null) {
    const student = studentId || localStorage.getItem('studentId');
    if (!student) {
      throw new Error('Student ID not found');
    }
    
    try {
      // Get student's batches first
      const batches = await this.request(`/batches/batches/?student=${student}`);
      
      // Get courses from batches
      const courses = [];
      for (const batch of batches.results || batches) {
        if (batch.course) {
          const course = await this.getCourseDetails(batch.course);
          courses.push({
            ...course,
            batch_id: batch.id,
            batch_name: batch.name,
            enrollment_date: batch.start_date,
            class_type: batch.class_type
          });
        }
      }
      
      return courses;
    } catch (error) {
      console.error('Failed to get student courses:', error);
      return [];
    }
  }

  // Get student's dashboard stats
  async getStudentStats(studentId = null) {
    const student = studentId || localStorage.getItem('studentId');
    if (!student) {
      throw new Error('Student ID not found');
    }
    
    try {
      const courses = await this.getStudentCourses(student);
      const completedCourses = courses.filter(course => course.progress === 100).length;
      const totalCourses = courses.length;
      
      // Placeholder values until backend implementation is ready
      const studyStreak = 0;
      
      // Calculate total hours (placeholder - would need backend implementation)
      const totalHours = courses.reduce((acc, course) => {
        return acc + (course.duration_months * 20); // Estimate 20 hours per month
      }, 0);
      
      return {
        totalCourses,
        completedCourses,
        studyStreak,
        totalHours,
        badges: 0,
        rank: totalCourses > 3 ? "Advanced Learner" : "Learner"
      };
    } catch (error) {
      console.error('Failed to get student stats:', error);
      return {
        totalCourses: 0,
        completedCourses: 0,
        studyStreak: 0,
        totalHours: 0,
        badges: 0,
        rank: "Learner"
      };
    }
  }

  // Check if student is enrolled in a specific course
  async isStudentEnrolledInCourse(courseId, studentId = null) {
    const student = studentId || localStorage.getItem('studentId');
    if (!student) {
      return false;
    }
    
    try {
      const courses = await this.getStudentCourses(student);
      return courses.some(course => course.id === courseId);
    } catch (error) {
      console.error('Failed to check enrollment:', error);
      return false;
    }
  }

  // Get student's enrollment status for a course
  async getEnrollmentStatus(courseId, studentId = null) {
    const student = studentId || localStorage.getItem('studentId');
    if (!student) {
      throw new Error('Student ID not found');
    }
    
    try {
      const isEnrolled = await this.isStudentEnrolledInCourse(courseId, student);
      return {
        isEnrolled,
        studentId: student,
        courseId
      };
    } catch (error) {
      console.error('Failed to get enrollment status:', error);
      return {
        isEnrolled: false,
        studentId: student,
        courseId
      };
    }
  }

  // STUDENT LIVE SESSIONS
  async getMyLiveSessions() {
    return this.request('/courses/live-sessions/my_sessions/');
  }

  async joinLiveSession(sessionId) {
    return this.request(`/courses/live-sessions/${sessionId}/join/`, {
      method: 'POST',
    });
  }

  async getLiveSessionDetails(sessionId) {
    return this.request(`/courses/live-sessions/${sessionId}/`);
  }

  // COURSE ENDPOINTS (Public and Student Access)
  
  // Get all available courses (public access - no authentication required)
  // Check if cache is still valid
  _isCacheValid() {
    if (!this.coursesCache.data || !this.coursesCache.timestamp) {
      return false;
    }
    const now = Date.now();
    return (now - this.coursesCache.timestamp) < this.coursesCache.ttl;
  }

  // Clear courses cache
  clearCoursesCache() {
    this.coursesCache.data = null;
    this.coursesCache.timestamp = null;
  }

  // Prefetch courses in the background (refreshes cache without blocking)
  async prefetchCourses() {
    try {
      await this.getCourses({}, false); // Force fresh fetch
    } catch (error) {
      console.warn('Background prefetch failed:', error);
    }
  }

  async getCourses(params = {}, useCache = true) {
    // Return cached data if valid and no search/filter params
    const hasFilters = Object.keys(params).length > 0;
    if (useCache && !hasFilters && this._isCacheValid()) {
      return this.coursesCache.data;
    }

    try {
      const searchParams = new URLSearchParams(params);
      const allCourses = [];
      let page = 1;
      let hasNext = true;
      
      // Fetch all pages to get all courses
      // Use larger page size to reduce number of requests
      while (hasNext) {
        const pageParams = new URLSearchParams(searchParams);
        pageParams.set('page', page.toString());
        pageParams.set('page_size', '200'); // Increased from 100 to reduce requests
        
        const response = await this.publicRequest(`/courses/courses/?${pageParams}`);
        
        if (response.results && response.results.length > 0) {
          allCourses.push(...response.results);
          hasNext = response.next !== null;
          page++;
        } else {
          hasNext = false;
        }
      }
      
      // Cache the results if no filters were applied
      if (!hasFilters) {
        this.coursesCache.data = allCourses;
        this.coursesCache.timestamp = Date.now();
      }
      
      return allCourses;
    } catch (error) {
      // Return cached data if available, even if stale
      if (this.coursesCache.data) {
        console.warn('Failed to fetch fresh courses, using cached data:', error);
        return this.coursesCache.data;
      }
      console.warn('Failed to fetch courses from API, using fallback data:', error);
      return this.getFallbackCourses();
    }
  }

  // Get course details (public access)
  async getCourseDetails(courseId) {
    try {
      return await this.publicRequest(`/courses/courses/${courseId}/`);
    } catch (error) {
      console.warn('Failed to fetch course details from API, using fallback data:', error);
      // Return fallback course data for the specific course ID
      const fallbackCourses = this.getFallbackCourses();
      const fallbackCourse = fallbackCourses.find(course => course.id === courseId);
      if (fallbackCourse) {
        return fallbackCourse;
      }
      // If no fallback course found, return a generic one
      return {
        id: courseId,
        name: 'Course Not Available',
        description: 'This course is currently unavailable. Please try again later.',
        duration_months: 0,
        cost_amount: 0,
        cost_currency: 'NGN',
        total_students_enrolled: 0,
        instructor_name: 'Aitech Instructor'
      };
    }
  }

  // Get course modules (public access)
  async getCourseModules(courseId) {
    return this.publicRequest(`/courses/courses/${courseId}/modules/`);
  }

  // Get PUBLIC materials for a course (no auth required)
  async getPublicMaterialsByCourse(courseId) {
    try {
      const res = await this.publicRequest(`/materials/materials/public/?course=${courseId}`);
      return Array.isArray(res) ? res : (res.results || []);
    } catch (e) {
      console.warn('Failed to load public materials by course:', e);
      return [];
    }
  }

  // Get course batches (public access)
  async getCourseBatches(courseId) {
    return this.publicRequest(`/courses/courses/${courseId}/batches/`);
  }

  // ENROLLMENT ENDPOINTS

  // Enroll student in a batch
  async enrollInBatch(batchId, studentId = null) {
    const student = studentId || localStorage.getItem('studentId');
    if (!student) {
      throw new Error('Student ID not found');
    }
    
    return this.request(`/batches/batches/${batchId}/enroll/`, {
      method: 'POST',
      body: JSON.stringify({ student_id: student }),
    });
  }

  // Get enrollment status for a course
  async getEnrollmentStatus(courseId, studentId = null) {
    const student = studentId || localStorage.getItem('studentId');
    if (!student) {
      throw new Error('Student ID not found');
    }
    
    return this.request(`/accounts/students/${student}/enrollment-status/${courseId}/`);
  }

  // MATERIALS ENDPOINTS

  // Get materials for a specific module
  async getModuleMaterials(moduleId) {
    return this.request(`/courses/modules/${moduleId}/materials/`);
  }

  // Get all materials with filtering
  async getMaterials(params = {}) {
    const searchParams = new URLSearchParams(params);
    const response = await this.request(`/materials/materials/?${searchParams}`);
    return response.results || response;
  }

  // PROGRESS TRACKING ENDPOINTS (Would need to be implemented in backend)

  // Update lesson completion
  async updateLessonProgress(lessonId, completed = true) {
    const studentId = localStorage.getItem('studentId');
    if (!studentId) {
      throw new Error('Student ID not found');
    }
    
    return this.request(`/progress/lessons/${lessonId}/`, {
      method: 'POST',
      body: JSON.stringify({ 
        student_id: studentId,
        completed: completed,
        completed_at: new Date().toISOString()
      }),
    });
  }

  // Get student progress for a course
  async getCourseProgress(courseId) {
    const studentId = localStorage.getItem('studentId');
    if (!studentId) {
      throw new Error('Student ID not found');
    }
    
    return this.request(`/progress/courses/${courseId}/student/${studentId}/`);
  }

  // Get student's completed lessons for a course
  async getStudentCompletedLessons(courseId) {
    try {
      const response = await this.request(`/progress/student-progress/completed_materials/?course_id=${courseId}`);
      return response || [];
    } catch (error) {
      console.warn('Failed to load completed lessons:', error);
      return [];
    }
  }

  // Update lesson progress
  async updateLessonProgress(materialId, completed = true, timeSpent = 0, notes = '') {
    try {
      const response = await this.request(`/progress/student-progress/`, {
        method: 'POST',
        body: JSON.stringify({
          material: materialId,
          completed: completed,
          time_spent: timeSpent,
          notes: notes
        }),
      });
      return response;
    } catch (error) {
      console.error('Failed to update lesson progress:', error);
      throw error;
    }
  }

  // Get course progress summary
  async getCourseProgressSummary(courseId) {
    try {
      const response = await this.request(`/progress/course-progress/?course=${courseId}`);
      return response.results?.[0] || null;
    } catch (error) {
      console.warn('Failed to load course progress summary:', error);
      return null;
    }
  }

  // STATISTICS ENDPOINTS (For landing page)

  // Get platform statistics (total students, courses, etc.)
  async getPlatformStats() {
    return this.request('/stats/platform/');
  }

  // Get course statistics
  async getCourseStats(courseId) {
    return this.request(`/stats/courses/${courseId}/`);
  }

  // TESTIMONIALS ENDPOINTS

  // Get testimonials for landing page
  async getTestimonials(params = {}) {
    const searchParams = new URLSearchParams(params);
    const response = await this.request(`/testimonials/?${searchParams}`);
    return response.results || response;
  }
}

// Create a singleton instance
const apiService = new ApiService();

export default apiService; 